import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Cake, Clock3, MapPin, X, NotebookPen, Check, MoreVertical, CalendarClock, Ban, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Screen, TopBar, BackButton, ScrollArea, BottomBar, PrimaryButton, SecondaryButton, TextArea } from "@/components/jk/ui";
import { Card, Pill, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { MonthCalendar, todayISO, isoPlusDays } from "@/components/jk/MonthCalendar";
import { formatDate, formatTime } from "@/lib/format";
import api from "@/lib/api";

const ClientBrief = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [completing, setCompleting] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState(null);

  const load = () => api.get(`/bookings/${id}`).then(({ data }) => { setB(data.booking); setNotes(data.booking.notes || ""); }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  if (loading) return <FullScreenLoader />;
  if (!b) return null;
  const c = b.client || {};
  const isCancelled = b.status === "cancelled";
  const isCompleted = b.status === "completed";
  const canManage = !isCancelled && !isCompleted;

  const saveNotes = async () => {
    await api.post(`/bookings/${id}/notes`, { notes });
    setShowNotes(false);
    load();
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/bookings/${id}/complete`, { notes });
      navigate("/dashboard");
    } catch { setCompleting(false); }
  };

  const cancelBooking = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled. Client notified.");
      setShowActions(false);
      load();
    } catch {
      toast.error("Could not cancel. Try again.");
    } finally { setBusy(false); }
  };

  const openReschedule = () => {
    setNewDate(b.booking_date);
    setNewTime(b.booking_time);
    setShowActions(false);
    setShowReschedule(true);
  };

  const confirmReschedule = async () => {
    if (!newDate || !newTime) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/reschedule`, { booking_date: newDate, booking_time: newTime });
      toast.success("Rescheduled. Client notified.");
      setShowReschedule(false);
      load();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "That slot is already taken. Pick another time." : "Could not reschedule.");
    } finally { setBusy(false); }
  };

  const generateBrief = async () => {
    setBriefLoading(true);
    setBriefError(null);
    try {
      const res = await api.post(`/astrologer/bookings/${id}/generate-brief`);
      setBrief(res.data.brief);
    } catch (err) {
      setBriefError("Could not generate brief. Please try again.");
    } finally {
      setBriefLoading(false);
    }
  };

  const renderBrief = (text) => {
    if (!text) return null
    const applyBold = (str) => {
      const parts = str.split(/\*\*(.+?)\*\*/)
      return parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      )
    }
    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) {
        return (
          <h2 key={i} className="font-semibold text-[16px] text-[#1A1A1A] mt-4 mb-1">
            {applyBold(line.slice(2))}
          </h2>
        )
      }
      if (line.startsWith('## ')) {
        return (
          <h3 key={i} className="font-semibold text-[15px] text-[#1A1A1A] mt-4 mb-1">
            {applyBold(line.slice(3))}
          </h3>
        )
      }
      if (line.trim() === '---') {
        return <hr key={i} className="border-t border-[#E9E7E1] my-3" />
      }
      if (line.startsWith('- ')) {
        return (
          <div key={i} className="flex gap-2 mb-1">
            <span className="text-[#888888] mt-[2px] text-[14px] shrink-0">•</span>
            <p className="text-[14px] text-[#333333] leading-relaxed">{applyBold(line.slice(2))}</p>
          </div>
        )
      }
      if (line.trim() === '') {
        return <div key={i} className="h-2" />
      }
      return (
        <p key={i} className="text-[14px] text-[#333333] leading-relaxed mb-1">
          {applyBold(line)}
        </p>
      )
    })
  }

  const DetailRow = ({ icon, label, value }) => (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f2f0ec] text-[#666666]">{icon}</span>
      <div><p className="text-[11px] uppercase tracking-wide text-[#aaaaaa]">{label}</p><p className="text-[14px] font-medium text-[#111111]">{value || "—"}</p></div>
    </div>
  );

  return (
    <Screen testid="client-brief-screen">
      <TopBar
        left={<BackButton onClick={() => navigate(-1)} />}
        center={<span className="text-[14px] font-semibold text-[#111111]">Client Brief</span>}
        right={canManage ? (
          <button data-testid="brief-actions-btn" onClick={() => setShowActions(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#111111] hover:bg-black/5">
            <MoreVertical size={20} strokeWidth={2} />
          </button>
        ) : null}
      />

      <ScrollArea className="px-6">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#111111]" data-testid="brief-client-name">{c.full_name}</h1>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="brief-badges">
          <Pill tone="periwinkle">{b.service_name}</Pill>
          {b.consultation_mode && <Pill tone="neutral">{b.consultation_mode}</Pill>}
          <Pill tone="neutral">{formatDate(b.booking_date)} · {formatTime(b.booking_time)}</Pill>
          {isCancelled && <Pill tone="dark">Cancelled</Pill>}
        </div>

        {/* Birth details */}
        <Card className="mt-5">
          <SectionLabel className="mb-4">Birth details</SectionLabel>
          <div className="space-y-4">
            <DetailRow icon={<Cake size={16} strokeWidth={1.75} />} label="Date of birth" value={formatDate(c.date_of_birth)} />
            <DetailRow icon={<Clock3 size={16} strokeWidth={1.75} />} label="Time of birth" value={c.time_of_birth} />
            <DetailRow icon={<MapPin size={16} strokeWidth={1.75} />} label="Place of birth" value={c.place_of_birth} />
          </div>
        </Card>

        {/* AI Consultation Brief */}
        <div className="mt-4">
          <button
            data-testid="generate-brief"
            onClick={generateBrief}
            disabled={briefLoading}
            className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] border border-[#111111] bg-white text-[15px] font-semibold text-[#111111] transition-all duration-150 ease-out active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={17} strokeWidth={2} />
            {briefLoading ? "Preparing brief..." : b?.brief_generated_at && !brief ? "Regenerate AI Brief" : "Generate AI Brief"}
          </button>
          {briefError && (
            <p className="mt-2 text-center text-[13px] text-[#d23b2f]" data-testid="brief-error">{briefError}</p>
          )}
          {b?.brief_generated_at && !brief && !briefLoading && (
            <p className="mt-2 text-center text-[12px] text-[#AAAAAA]" data-testid="brief-last-generated">
              Last generated {formatDate(b.brief_generated_at.split('T')[0])}
            </p>
          )}
        </div>

        {brief && (
          <Card className="mt-4" data-testid="ai-brief-card">
            <div className="mb-3 flex items-center gap-2">
              <SectionLabel>AI Brief</SectionLabel>
              <span className="rounded bg-purple-100 px-1 text-xs text-purple-700">AI</span>
            </div>
            <div className="space-y-0">{renderBrief(brief)}</div>
            <button
              data-testid="save-brief-as-notes"
              onClick={async () => { await api.post(`/bookings/${id}/notes`, { notes: brief }); load(); }}
              className="mt-3 text-[13px] font-medium text-[#666666] underline underline-offset-2"
            >
              Save as Notes
            </button>
          </Card>
        )}

        {/* Primary query (highlighted dark) */}
        <Card dark className="mt-4" data-testid="primary-query">
          <SectionLabel className="text-white/60">Primary query · {b.query_type}</SectionLabel>
          <p className="mt-3 text-[15px] leading-relaxed">{b.query_detail || "No specific query was shared."}</p>
        </Card>

        {/* Notes preview */}
        {b.notes && (
          <Card className="mt-4" data-testid="brief-notes">
            <SectionLabel className="mb-2">Your notes</SectionLabel>
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-[#333]">{b.notes}</p>
          </Card>
        )}

        {/* Past consultations */}
        <div className="mt-5 pb-6">
          <SectionLabel className="mb-3">Past consultations</SectionLabel>
          {(!b.past_consultations || b.past_consultations.length === 0) ? (
            <p className="rounded-[14px] border border-dashed border-[#e0ddd7] py-5 text-center text-[13px] text-[#aaaaaa]">First consultation with this client</p>
          ) : (
            <div className="space-y-2.5">
              {b.past_consultations.map((p) => (
                <div key={p.id} className="rounded-[14px] border border-[#e9e6e1] bg-white p-3.5">
                  <p className="text-[14px] font-semibold text-[#111111]">{p.service_name}</p>
                  <p className="text-[12px] text-[#666666]">{formatDate(p.booking_date)}</p>
                  {p.notes && <p className="mt-1.5 line-clamp-2 text-[12px] text-[#777]">{p.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <BottomBar>
        <div className="flex gap-3">
          <div className="flex-1">
            <SecondaryButton testid="add-notes" onClick={() => setShowNotes(true)} icon={<NotebookPen size={17} strokeWidth={2} />}>Notes</SecondaryButton>
          </div>
          <div className="flex-1">
            {isCompleted ? (
              <PrimaryButton testid="completed-state" disabled icon={<Check size={17} strokeWidth={2.5} />}>Completed</PrimaryButton>
            ) : isCancelled ? (
              <PrimaryButton testid="cancelled-state" disabled>Cancelled</PrimaryButton>
            ) : (
              <PrimaryButton testid="mark-complete" onClick={markComplete} disabled={completing}>{completing ? "…" : "Mark Complete"}</PrimaryButton>
            )}
          </div>
        </div>
      </BottomBar>

      {showNotes && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={() => setShowNotes(false)} data-testid="notes-sheet">
          <div className="w-full rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[20px] font-bold text-[#111111]">Consultation notes</h2>
              <button onClick={() => setShowNotes(false)} className="text-[#666666]"><X size={22} /></button>
            </div>
            <TextArea testid="notes-textarea" rows={6} maxLength={2000} placeholder="Record observations, remedies, predictions…" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="mt-5"><PrimaryButton testid="save-notes" onClick={saveNotes}>Save notes</PrimaryButton></div>
          </div>
        </div>
      )}

      {/* Actions sheet */}
      {showActions && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={() => setShowActions(false)} data-testid="actions-sheet">
          <div className="w-full rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[20px] font-bold text-[#111111]">Manage booking</h2>
              <button onClick={() => setShowActions(false)} className="text-[#666666]"><X size={22} /></button>
            </div>
            <div className="space-y-2.5">
              <SecondaryButton testid="action-reschedule" onClick={openReschedule} icon={<CalendarClock size={17} strokeWidth={2} />}>Reschedule</SecondaryButton>
              <button data-testid="action-cancel" onClick={cancelBooking} disabled={busy}
                className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] border border-[#d23b2f] bg-white text-[15px] font-semibold text-[#d23b2f] transition-all active:scale-[0.99] disabled:opacity-50">
                <Ban size={17} strokeWidth={2} /> {busy ? "Cancelling…" : "Cancel booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule sheet */}
      {showReschedule && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={() => setShowReschedule(false)} data-testid="reschedule-sheet">
          <div className="max-h-[88%] w-full overflow-y-auto rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[20px] font-bold text-[#111111]">Reschedule</h2>
              <button onClick={() => setShowReschedule(false)} className="text-[#666666]"><X size={22} /></button>
            </div>
            <SectionLabel className="mb-2">New date</SectionLabel>
            <MonthCalendar value={newDate} onSelect={setNewDate} minDate={todayISO()} maxDate={isoPlusDays(90)} testid="reschedule-calendar" />
            <div className="mt-4">
              <SectionLabel className="mb-2">New time</SectionLabel>
              <input type="time" data-testid="reschedule-time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
                className="h-[52px] w-full rounded-[12px] border border-[#e5e5e5] bg-[#f9f9f9] px-4 text-[14px] text-[#111111] outline-none focus:border-[#111111] focus:bg-white" />
            </div>
            <div className="mt-5">
              <PrimaryButton testid="reschedule-confirm" onClick={confirmReschedule} disabled={busy || !newDate || !newTime}>
                {busy ? "Saving…" : "Confirm reschedule"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
};

export default ClientBrief;
